import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getApplicationActionPermissions } from 'components/Applications/utils/getApplicationActionPermissions';
import EyeAttachmentPreview from 'components/EyeAttachmentPreview/EyeAttachmentPreview';
import IconButton from 'components/IconButton/IconButton';
import { useCardActions } from 'components/SelectCardWrapper/hooks/useCardActions';

import { useMapCardActionsToIconButtons } from 'hooks/useMapCardActionsToIconButtons';

import { Application } from 'types/applications';

import { JobseekerAttachmentTypes, SidebarContentTypes } from 'constants/enums';

import { ReactComponent as PersonalCardIcon } from 'assets/icons/personal-card.svg';

export const useApplicationActionButtons = ({
  application,
  isSplitView = false,
  onSplitViewOpen,
  isHideViewCv = false
}: {
  application?: Application | null;
  isSplitView?: boolean;
  onSplitViewOpen?: (applicationId: string) => void;
  isHideViewCv?: boolean;
}): {
  actionButtons: ReactNode[];
  activeComponent: ReactNode;
} => {
  const { t } = useTranslation();

  const { actions, activeComponent } = useCardActions({
    sidebarContentType: SidebarContentTypes.ApplicationView,
    permissions: getApplicationActionPermissions(application),
    contentId: application?.id,
    isSplitView,
    onSplitViewOpen
  });

  const jobseekerCV = application?.attachments?.find(
    (attachment) => attachment.type === JobseekerAttachmentTypes.CV
  );

  const actionButtons = useMapCardActionsToIconButtons(actions);

  const cvPreviewButton = jobseekerCV ? (
    <EyeAttachmentPreview
      key="view-cv"
      attachments={(application as Application).attachments || []}
      attachmentType={JobseekerAttachmentTypes.CV}
      renderButton={(onClick) => (
        <IconButton
          icon={<PersonalCardIcon />}
          onClick={onClick}
          tooltip={t('card.jobseeker.button.viewResume')}
        />
      )}
    />
  ) : null;

  return {
    actionButtons: isHideViewCv
      ? actionButtons
      : [cvPreviewButton, ...actionButtons],
    activeComponent
  };
};
